// ** react imports
import React, { useState } from 'react';
import PropTypes from 'prop-types';

// ** components imports
import Banner from '../../components/Banner';

const AccountDelete = props => {
  const [bannerWarningOpen, setBannerWarningOpen] = useState(true);

  return (
    <div className="flex">
      <main className="grow">
        <div className="px-4 sm:px-6 lg:px-8 py-6 w-full max-w-[96rem] mx-auto">
          {/* Page header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
              Account Deletion
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              This page explains how you can request account deletion for your
              Multitax account and what data will be deleted.
            </p>
          </div>

          {/* Warning Banner */}
          <Banner
            type="warning"
            open={bannerWarningOpen}
            setOpen={setBannerWarningOpen}
          >
            <strong>Warning:</strong> Account deletion is permanent and cannot
            be undone. Once deleted, you will not be able to recover your
            account or any associated data.
          </Banner>

          {/* Main Content */}
          <div className="mt-8 space-y-8">
            {/* How to Delete Section */}
            <div className="bg-white dark:bg-gray-800 shadow-xs rounded-xl p-6 border border-gray-200 dark:border-gray-700/60">
              <div className="mb-6">
                <h2 className="text-xl text-gray-800 dark:text-gray-100 font-bold mb-4">
                  How to Delete Your Account
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  To request deletion of your account and associated data,
                  follow these steps:
                </p>
              </div>

              <ol className="space-y-3 text-gray-600 dark:text-gray-300 mb-6">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">1.</span>
                  <span>Open the Multitax mobile app on your device</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">2.</span>
                  <span>
                    Navigate to your Profile by tapping the profile icon
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">3.</span>
                  <span>Tap on &quot;Settings&quot; or the settings icon</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">4.</span>
                  <span>Scroll down and tap &quot;Delete Account&quot;</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">5.</span>
                  <span>Confirm your decision when prompted</span>
                </li>
              </ol>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 mb-6">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 mt-0.5 text-blue-600 dark:text-blue-400 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-1">
                      Note
                    </h4>
                    <p className="text-blue-700 dark:text-blue-300 text-sm">
                      You must be logged in to delete your account.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* What Data Will Be Deleted Section */}
            <div className="bg-white dark:bg-gray-800 shadow-xs rounded-xl p-6 border border-gray-200 dark:border-gray-700/60">
              <h2 className="text-xl text-gray-800 dark:text-gray-100 font-bold mb-4">
                What Data Will Be Deleted
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                When you delete your account, the following data will be
                permanently deleted:
              </p>

              <ul className="space-y-3 text-gray-600 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold">•</span>
                  <span>
                    <strong>Account Information:</strong> Your email, username,
                    name, and profile picture
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold">•</span>
                  <span>
                    <strong>Authentication Data:</strong> Your Firebase
                    authentication credentials
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold">•</span>
                  <span>
                    <strong>Tax Returns and Filings:</strong> All tax returns,
                    forms, calculations, and attachments you&apos;ve created
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold">•</span>
                  <span>
                    <strong>Financial Documents:</strong> All uploaded invoices,
                    receipts, and financial records
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold">•</span>
                  <span>
                    <strong>Client Data:</strong> All client profiles, cases,
                    and associated tax information
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold">•</span>
                  <span>
                    <strong>Notifications:</strong> Push notification history
                    and preferences
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold">•</span>
                  <span>
                    <strong>Device Data:</strong> FCM tokens and device metadata
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold">•</span>
                  <span>
                    <strong>Activity History:</strong> Tax filing history,
                    compliance reports, and audit logs
                  </span>
                </li>
              </ul>
            </div>

            {/* Data Retention Section */}
            <div className="bg-white dark:bg-gray-800 shadow-xs rounded-xl p-6 border border-gray-200 dark:border-gray-700/60">
              <h2 className="text-xl text-gray-800 dark:text-gray-100 font-bold mb-4">
                Data Retention
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                After account deletion:
              </p>

              <ul className="space-y-3 text-gray-600 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-gray-400 mr-3">▪</span>
                  <span>
                    All personal data is immediately and permanently deleted
                    from our systems
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-3">▪</span>
                  <span>
                    No data is retained in backups after the standard backup
                    retention period (30 days)
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-3">▪</span>
                  <span>
                    Some anonymized analytics data may be retained for service
                    improvement purposes, but this data cannot be linked back to
                    you
                  </span>
                </li>
              </ul>
            </div>

            {/* Alternative Section */}
            <div className="bg-white dark:bg-gray-800 shadow-xs rounded-xl p-6 border border-gray-200 dark:border-gray-700/60">
              <h2 className="text-xl text-gray-800 dark:text-gray-100 font-bold mb-4">
                Alternative: Deactivating Your Account
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                If you&apos;re not sure about permanent deletion, you can also
                consider:
              </p>

              <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-indigo-500 mr-3">▪</span>
                  <span>Logging out of the app temporarily</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-500 mr-3">▪</span>
                  <span>
                    Disabling push notifications in your device settings
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-500 mr-3">▪</span>
                  <span>
                    Contacting our support team for other account management
                    options
                  </span>
                </li>
              </ul>
            </div>

            {/* Need Help Section */}
            <div className="bg-white dark:bg-gray-800 shadow-xs rounded-xl p-6 border border-gray-200 dark:border-gray-700/60">
              <h2 className="text-xl text-gray-800 dark:text-gray-100 font-bold mb-4">
                Need Help?
              </h2>
              <div className="space-y-4 text-gray-600 dark:text-gray-300">
                <p>
                  If you have questions about account deletion or need
                  assistance, please contact our support team at{' '}
                  <a
                    href="mailto:support@multitaxx.com"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    support@multitaxx.com
                  </a>
                  .
                </p>
                <p>
                  For privacy-related inquiries, you can also refer to our
                  Privacy Policy in the app.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center py-4">
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                © 2025 Multitax. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

AccountDelete.propTypes = {};

export default AccountDelete;
